"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createSystemNodeUid = void 0;
exports.createNode = createNode;
exports.createRootNode = createRootNode;
exports.createDefaultLayoutNode = createDefaultLayoutNode;
exports.createContainerNode = createContainerNode;
exports.createModelNode = createModelNode;
exports.createRouteSegmentNode = createRouteSegmentNode;
exports.createDefaultFootprintNode = createDefaultFootprintNode;
exports.createSystemNode = createSystemNode;
exports.createNetNode = createNetNode;
exports.createElementNode = createElementNode;
exports.createPadNodeFromGenesisTerminalElement = createPadNodeFromGenesisTerminalElement;
exports.createRootFootprintNode = createRootFootprintNode;
exports.createDiminishedNode = createDiminishedNode;
exports.createShapesContainerNode = createShapesContainerNode;
exports.createMountsContainerNode = createMountsContainerNode;
exports.createPadNodeUnderMountsContainer = createPadNodeUnderMountsContainer;
exports.createSilkShapesContainerNode = createSilkShapesContainerNode;
exports.createEcoShapesContainerNode = createEcoShapesContainerNode;
exports.createFabShapesContainerNode = createFabShapesContainerNode;
exports.createTextShapeUnderShapesContainer = createTextShapeUnderShapesContainer;
exports.createVirtualViaNode = createVirtualViaNode;
exports.createVirtualSmartViaNode = createVirtualSmartViaNode;
exports.createVirtualRouteSegmentNode = createVirtualRouteSegmentNode;
exports.createVirtualFillNode = createVirtualFillNode;
exports.createVirtualModelNode = createVirtualModelNode;
const tslib_1 = require("tslib");
const shared_1 = require("@buildwithflux/shared");
const mutative_1 = require("mutative");
const spark_md5_1 = tslib_1.__importDefault(require("spark-md5"));
const element_1 = require("../../element");
const net_1 = require("../../net");
const bakedRule_1 = require("../bakedRule");
const config_1 = require("../config");
const rule_1 = require("../rule");
const structure_1 = require("./structure");
const POSITION_FACTOR = 1000;
const createSystemNodeUid = (parentUid, name) => {
    return `${parentUid}.${name}`;
};
exports.createSystemNodeUid = createSystemNodeUid;
function createNode(type, name, fields, rules) {
    return {
        ...fields,
        uid: fields.uid ?? (0, shared_1.guid)(),
        parentUid: fields.parentUid ?? structure_1.pcbLayoutRootNodeId,
        childUids: fields.childUids ?? [],
        type,
        name,
        pcbNodeRuleSet: fields.pcbNodeRuleSet ?? (0, rule_1.createNodeRuleSet)(rules ?? {}),
    };
}
/**
 * NOTE: it is assumed everywhere that the root node always has the same uid and
 * parentUid.
 */
function createRootNode() {
    return createNode(structure_1.PcbNodeTypes.root, "Root", {
        uid: structure_1.pcbLayoutRootNodeId,
        parentUid: structure_1.pcbLayoutRootNodeParentId,
    });
}
function createDefaultLayoutNode(documentUid) {
    return createNode(structure_1.PcbNodeTypes.layout, "Layout", {
        uid: `${documentUid}.default${structure_1.PcbNodeTypes.layout}`,
        parentUid: structure_1.pcbLayoutRootNodeId,
    }, 
    // Create an object-specific "stackup" rule for the new layout node
    // with an empty value to make editing the stackup more accessible.
    {
        stackup: undefined,
    });
}
function createContainerNode(parentUid, name) {
    return createNode(structure_1.PcbNodeTypes.container, name, {
        uid: (0, exports.createSystemNodeUid)(parentUid, name),
        parentUid,
    });
}
function createModelNode(parentUid, name) {
    return createNode(structure_1.PcbNodeTypes.model, name, {
        uid: (0, exports.createSystemNodeUid)(parentUid, name),
        parentUid,
    });
}
function createRouteSegmentNode(startId, endId, startPosition, endPosition, parentUid, layer, humanizedTraceWidth, humanizedCenter, parentLayer) {
    const traceBottom = layer === bakedRule_1.LayerOrientation.bottom;
    const parentBottom = parentLayer === bakedRule_1.LayerOrientation.bottom;
    // Invert only if the parent orientation is different than the trace one
    const invertLayer = (traceBottom && !parentBottom) || (!traceBottom && parentBottom) ? -1 : 1;
    let rules = {
        startPosition: `${startPosition.x * invertLayer * POSITION_FACTOR} ${startPosition.y * POSITION_FACTOR}`,
        endPosition: `${endPosition.x * invertLayer * POSITION_FACTOR} ${endPosition.y * POSITION_FACTOR}`,
        layer: layer, // the value this takes is linked to the values that are possible in the dropdown menu
        position: humanizedCenter,
    };
    if (humanizedTraceWidth) {
        rules = {
            ...rules,
            traceWidth: humanizedTraceWidth,
        };
    }
    return createNode(structure_1.PcbNodeTypes.routeSegment, "Trace", { parentUid, startId, endId }, rules);
}
function createDefaultFootprintNode(documentUid) {
    return createNode(structure_1.PcbNodeTypes.footprint, "Footprint", {
        uid: `${documentUid}.defaultFootprint`,
        parentUid: structure_1.pcbLayoutRootNodeId,
    });
}
function createSystemNode(type, name, parentUid, fields = {}, rules) {
    return createNode(type, name, {
        uid: fields.uid ?? (0, exports.createSystemNodeUid)(parentUid, name),
        parentUid,
        ...fields,
    }, rules);
}
function createNetNode(uid, parentUid, name = "Net", terminals) {
    return createNode(structure_1.PcbNodeTypes.net, name, {
        uid,
        parentUid,
        terminals: net_1.Net.terminalsToMap(terminals.topLevel),
        submoduleTerminals: net_1.Net.terminalsToMap(terminals.submodule),
    });
}
function createElementNode(element, parentUid) {
    return createNode(structure_1.PcbNodeTypes.element, element.label || element.part_version_data_cache?.name || "", {
        uid: element.uid,
        parentUid,
    });
}
function createPadNodeFromGenesisTerminalElement(element, parentUid) {
    return createNode(structure_1.PcbNodeTypes.pad, element.label || element.part_version_data_cache?.name || "", {
        uid: element.uid,
        parentUid,
        // See note re terminalId field (which should be named pinNumber) in the type definition of PcbNodeTypes.pad
        terminalId: (0, element_1.getPinNumberProperty)(element)?.value || "",
    });
}
function createRootFootprintNode(documentUid) {
    return createNode(structure_1.PcbNodeTypes.footprint, "Footprint", {
        uid: `${documentUid}.defaultFootprint`,
        parentUid: structure_1.pcbLayoutRootNodeId,
    });
}
function createDiminishedNode(node) {
    const nodeFields = (0, mutative_1.create)(node, (draft) => {
        if ("virtualNodeInfo" in draft) {
            delete draft.virtualNodeInfo;
        }
        if ("bakedRules" in draft) {
            delete draft.bakedRules;
        }
        if ("flattenedRules" in draft) {
            delete draft.flattenedRules;
        }
    });
    return createNode(node.type, node.name, nodeFields);
}
// =====================================================
//
// Below are factory functions for virtual nodes
//
// =====================================================
function createVirtualNode(type, name, parentUid, originRule, originNodeUid, fields, rules) {
    // TODO: [NodeType] - fix node type here, virtual node info is now part
    // of baked node
    return createSystemNode(type, name, parentUid, {
        // NOTE: Only virtual nodes should have virtualNodeInfo defined
        virtualNodeInfo: { originRule, originNodeUid },
        ...fields,
    }, rules);
}
/**
 * This is to create a virtual `Shapes` node. Normally this node will be the direct child of a footprint node, and there is only one `Shapes` node per
 * footprint, so its uid is guaranteed to be unique given a `parentNodeUid`
 * @param parentNodeUid uid of the direct parent node for the Shapes node, normally this is the uid of the footprint node
 * @returns
 */
function createShapesContainerNode(parentNodeUid) {
    return createVirtualNode(structure_1.PcbNodeTypes.container, "Shapes", parentNodeUid, "footprintAsset", parentNodeUid, {
        uid: `${parentNodeUid}.shapes`,
    });
}
/**
 * This is to create a virtual `Mounts` node. Normally this node will be the direct child of a footprint node, and there is only one `Shapes` node per
 * footprint, so its uid is guaranteed to be unique given a `parentNodeUid`
 * @param parentNodeUid uid of the direct parent node for the Shapes node, normally this is the uid of the footprint node
 * @returns
 */
function createMountsContainerNode(parentNodeUid) {
    return createVirtualNode(structure_1.PcbNodeTypes.container, "Mounts", parentNodeUid, "footprintAsset", parentNodeUid, {
        uid: `${parentNodeUid}.mounts`,
    });
}
function createPadNodeUnderMountsContainer(padUid, padName, mountsContainerNodeUid, footprintNodeUid) {
    return createVirtualNode(structure_1.PcbNodeTypes.pad, padName, mountsContainerNodeUid, "footprintAsset", footprintNodeUid, {
        uid: `${mountsContainerNodeUid}.${padUid}`,
    });
}
function createSilkShapesContainerNode(parentNodeUid, footprintNodeUid, svgPaths) {
    return createVirtualNode(structure_1.PcbNodeTypes.line, "Silk Shapes", parentNodeUid, "footprintAsset", footprintNodeUid, {
        uid: `${parentNodeUid}_${spark_md5_1.default.hash(svgPaths.join(""))}`,
        pcbNodeRuleSet: {
            asset: {
                key: config_1.LayoutRules.asset.key,
                uid: "asset",
                value: "Silk Shapes",
                payloadFileName: "SilkShapes.svg",
                payloadAsBase64: btoa(`<svg xmlns="http://www.w3.org/2000/svg">${svgPaths.join("")}</svg>`),
                order: 0,
            },
        },
    });
}
function createEcoShapesContainerNode(parentNodeUid, footprintNodeUid, svgPaths) {
    return createVirtualNode(structure_1.PcbNodeTypes.line, "Eco Shapes", parentNodeUid, "footprintAsset", footprintNodeUid, {
        uid: `${parentNodeUid}_${spark_md5_1.default.hash(svgPaths.join(""))}`,
        pcbNodeRuleSet: {
            asset: {
                key: config_1.LayoutRules.asset.key,
                uid: "asset",
                value: "Eco Shapes",
                payloadFileName: "EcoShapes.svg",
                payloadAsBase64: btoa(`<svg xmlns="http://www.w3.org/2000/svg">${svgPaths.join("")}</svg>`),
                order: 0,
            },
        },
    });
}
function createFabShapesContainerNode(parentNodeUid, footprintNodeUid, svgPaths) {
    return createVirtualNode(structure_1.PcbNodeTypes.line, "Fab Shapes", parentNodeUid, "footprintAsset", footprintNodeUid, {
        uid: `${parentNodeUid}_${spark_md5_1.default.hash(svgPaths.join(""))}`,
        pcbNodeRuleSet: {
            asset: {
                key: config_1.LayoutRules.asset.key,
                uid: "asset",
                value: "Fab Shapes",
                payloadFileName: "FabShapes.svg",
                payloadAsBase64: btoa(`<svg xmlns="http://www.w3.org/2000/svg">${svgPaths.join("")}</svg>`),
                order: 0,
            },
        },
    });
}
function createTextShapeUnderShapesContainer(text, shapesContainerNodeUid, shapeUid, footprintNodeUid) {
    return createVirtualNode(structure_1.PcbNodeTypes.text, text, shapesContainerNodeUid, "footprintAsset", footprintNodeUid, {
        uid: shapeUid,
    });
}
/**
 * Creates a virtual via node. Virtual via nodes are created when generating
 * fill virtual nodes
 *
 * @param viaName - name of the via node
 * @param parentUid - uid of its parent node, it should be the fill node
 * @param originNodeUid - uid of the node originating copper fill, it should be the Net node
 * @param fields - other custmized fields
 */
function createVirtualViaNode(viaName, parentUid, originNodeUid, virtualNodeOriginRule, fields) {
    return createVirtualNode(structure_1.PcbNodeTypes.via, viaName, parentUid, virtualNodeOriginRule, originNodeUid, fields);
}
function createVirtualSmartViaNode(virtualNodeOriginRule, smartViaUid, smartViaName, parentUid, originNodeUid, fields, rules) {
    return createVirtualNode(structure_1.PcbNodeTypes.smartVia, smartViaName, parentUid, virtualNodeOriginRule, originNodeUid, { uid: smartViaUid, ...fields }, rules);
}
function createVirtualRouteSegmentNode(virtualNodeOriginRule, uid, startId, endId, startPosition, endPosition, parentUid, layer, humanizedTraceWidth, humanizedCenter, parentLayer, name = "Trace", fields = {}, additionalRules = {}) {
    const traceBottom = layer === bakedRule_1.LayerOrientation.bottom;
    const parentBottom = parentLayer === bakedRule_1.LayerOrientation.bottom;
    // Invert only if the parent orientation is different than the trace one
    const invertLayer = (traceBottom && !parentBottom) || (!traceBottom && parentBottom) ? -1 : 1;
    const rules = {
        startPosition: `${startPosition.x * invertLayer * POSITION_FACTOR} ${startPosition.y * POSITION_FACTOR}`,
        endPosition: `${endPosition.x * invertLayer * POSITION_FACTOR} ${endPosition.y * POSITION_FACTOR}`,
        layer: layer, // the value this takes is linked to the values that are possible in the dropdown menu
        position: humanizedCenter,
        ...additionalRules,
    };
    if (humanizedTraceWidth) {
        rules.traceWidth = humanizedTraceWidth;
    }
    return createVirtualNode(structure_1.PcbNodeTypes.routeSegment, name, parentUid, virtualNodeOriginRule, parentUid, { ...fields, uid, startId, endId }, rules);
}
/**
 * Creates a virtual fill node. Virtual fill node is created when generating
 * fill virtual nodes

 * @param parentUid - uid of its parent node, it should be the Net node
 * @param originNodeUid - uid of the node originating copper fill, it should be the Net node
 * @param fields - other custmized fields
 */
function createVirtualFillNode(parentUid, originNodeUid, fields) {
    return createVirtualNode(structure_1.PcbNodeTypes.fill, "Fill", parentUid, "fill", originNodeUid, fields);
}
function createVirtualModelNode(uid, parentUid, originNodeUid) {
    return createVirtualNode(structure_1.PcbNodeTypes.model, "Model", parentUid, "footprintAsset", originNodeUid, { uid });
}
//# sourceMappingURL=factory.js.map